import { Box, Stack, Typography } from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ChartEditorPanel from "./ChartEditorPanel";
import JsonCodeEditor from "./JsonCodeEditor";
import { AppFormField, AppFormGrid } from "./layout";
import { AppAutocomplete, AppTextField } from "./form";
import { GRID_FULL } from "../lib/ui/formLayout";
import {
  mappingToJson,
  parseWidgetChartMapping,
} from "../utils/chartMappingBuilder";
import {
  defaultMappingForFormat,
  type DraftOutputFormat,
  mappingToEditorText,
  parseMappingEditorText,
} from "../utils/widgetDraftApproval";
import { DEFAULT_TEXT_TEMPLATE, OUTPUT_FORMAT_OPTIONS } from "../utils/widgetDraftDefaults";
import { useAppCustomStyles } from "../assets/theme/custom/hooks";

function isValidJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

type Props = {
  format: DraftOutputFormat;
  mapping: Record<string, unknown>;
  widgetName?: string;
  onChange: (next: { format: DraftOutputFormat; mapping: Record<string, unknown> }) => void;
  disabled?: boolean;
  /** When true, omit outer panel chrome (used inside WidgetDraftApprovalEditor). */
  embedded?: boolean;
};

export default function WidgetDraftOutputEditor({
  format,
  mapping,
  widgetName,
  onChange,
  disabled,
  embedded = false,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;

  const formatOption =
    OUTPUT_FORMAT_OPTIONS.find((o) => o.value === format) ?? OUTPUT_FORMAT_OPTIONS[0];

  const chartMapping = useMemo(() => parseWidgetChartMapping(mapping), [mapping]);

  const [tableMappingText, setTableMappingText] = useState(() => mappingToEditorText(mapping));
  useEffect(() => {
    if (format === "table") {
      setTableMappingText(mappingToEditorText(mapping));
    }
  }, [format, mapping]);
  const mappingJsonError = format === "table" && !isValidJson(tableMappingText);

  const onFormatChange = useCallback(
    (nextFormat: DraftOutputFormat) => {
      onChange({
        format: nextFormat,
        mapping: defaultMappingForFormat(nextFormat, widgetName),
      });
    },
    [onChange, widgetName]
  );

  const onChartMappingChange = useCallback(
    (next: ReturnType<typeof parseWidgetChartMapping>) => {
      onChange({
        format: "echarts",
        mapping: JSON.parse(mappingToJson(next)) as Record<string, unknown>,
      });
    },
    [onChange]
  );

  const templateValue =
    mapping.template != null ? String(mapping.template) : DEFAULT_TEXT_TEMPLATE;

  const content = (
    <AppFormGrid>
      {!embedded && (
        <AppFormField size={GRID_FULL}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={styles.draftOutputTitle}>
              {t("widget.draftOutputApprovalTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("widget.draftOutputApprovalHint")}
            </Typography>
          </Stack>
        </AppFormField>
      )}

      {embedded && (
        <AppFormField size={GRID_FULL}>
          <Typography variant="caption" sx={styles.draftOutputEmbeddedLabel}>
            {t("widget.outputFormat")}
          </Typography>
        </AppFormField>
      )}

      <AppFormField size={GRID_FULL}>
        <AppAutocomplete<(typeof OUTPUT_FORMAT_OPTIONS)[number]>
          label={t("widget.outputFormat")}
          options={OUTPUT_FORMAT_OPTIONS}
          value={formatOption}
          onChange={(_, opt) => {
            if (!opt || disabled) return;
            onFormatChange(opt.value);
          }}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          disableClearable
          size="small"
          disabled={disabled}
        />
      </AppFormField>

      {format === "echarts" && (
        <AppFormField size={GRID_FULL}>
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={styles.draftOutputChartHeader}>
              <BarChartOutlinedIcon fontSize="small" color="primary" />
              <Typography variant="caption" sx={styles.draftOutputTitle}>
                {t("chartEditor.title")}
              </Typography>
            </Stack>
            <ChartEditorPanel
              mapping={chartMapping}
              onChange={onChartMappingChange}
              disabled={disabled}
            />
          </Box>
        </AppFormField>
      )}

      {format === "table" && (
        <AppFormField size={GRID_FULL}>
          <JsonCodeEditor
            label={t("widget.outputMapping")}
            value={tableMappingText}
            onChange={(text) => {
              if (disabled) return;
              setTableMappingText(text);
              if (!isValidJson(text)) return;
              try {
                onChange({ format: "table", mapping: parseMappingEditorText(text) });
              } catch {
                // invalid object shape; keep local text
              }
            }}
            height={200}
            error={mappingJsonError}
            helperText={t("widget.draftTableMappingHint")}
            readOnly={disabled}
          />
        </AppFormField>
      )}

      {(format === "text" || format === "html") && (
        <AppFormField size={GRID_FULL}>
          <AppTextField
            label={t("widget.draftTemplateLabel")}
            value={templateValue}
            onChange={(e) =>
              onChange({ format, mapping: { template: e.target.value } })
            }
            multiline
            minRows={3}
            size="small"
            disabled={disabled}
            helperText={t("widget.draftTemplateHint")}
          />
        </AppFormField>
      )}
    </AppFormGrid>
  );

  if (embedded) {
    return <Box sx={styles.draftOutputEmbedded}>{content}</Box>;
  }

  return (
    <Box sx={styles.draftOutputWarningPanel}>
      {content}
    </Box>
  );
}
